import { useState } from "react";
import { Activity, AlertOctagon, Check, CircleDot, FlaskConical, RefreshCw, RotateCcw, Settings2, ShieldCheck, Wifi, X } from "lucide-react";
import { get, getErrorMessage, post } from "../lib/api";
import { getAccountRuntimeTruth } from "../lib/accountRuntime";
import { formatUtcTime, formatIndianTime } from "../lib/operatorFormatters";

export default function Header({
  status,
  refresh,
  onBacktest,
  onSettings,
  lastUpdated,
  accounts = [],
  accountsError = null,
  selectedViewAccountId = "paper",
  onSelectViewAccount,
}) {
  const [notice, setNotice] = useState(null);
  const [confirmKill, setConfirmKill] = useState(false);
  const [confirmResume, setConfirmResume] = useState(false);
  const [confirmStart, setConfirmStart] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);
  const [switchingAccount, setSwitchingAccount] = useState(false);
  const runtimeTruth = getAccountRuntimeTruth(status);
  const running = runtimeTruth.running;
  const mode = runtimeTruth.accountType;
  const kill = status?.risk?.kill_switch;
  const needsReconciliation = runtimeTruth.reconciliationBlocked;
  const accountType = runtimeTruth.accountType;
  const accountPathId = encodeURIComponent(selectedViewAccountId);
  const updatedIst = formatIndianTime(lastUpdated);
  const updatedUtc = formatUtcTime(lastUpdated);
  const isPaperAccount = selectedViewAccountId === "paper" || status?.mode === "paper";
  const feedState = !status
    ? "Status pending"
    : !running
      ? isPaperAccount ? "Paper engine stopped" : "Engine stopped"
      : status.websocket_connected
        ? "Market feed live"
        : "REST price feed";
  const feedTone = !status || !running
    ? "paused"
    : "connected";

  const act = async (path, label) => {
    // ponytail: optimistic immediate feedback — show "sending" instantly, don't block UI
    setNotice({ ok: true, text: `${label || "Action"} sent…` });
    try {
      await post(path);
      setNotice({ ok: true, text: `${label || "Action"} completed` });
    } catch (error) {
      setNotice({ ok: false, text: getErrorMessage(error) });
    }
    refresh?.();
  };

  const verifyExchange = async () => {
    if (isPaperAccount) {
      setNotice({
        ok: true,
        text: "Paper simulator selected · no Binance connection is used",
      });
      return;
    }
    try {
      const response = await get(`/accounts/${accountPathId}/status`);
      const account = response.data?.exchange || {};
      setNotice({
        ok: account.connected,
        text: account.connected
          ? `Binance ${account.account_type} connected · can trade: ${account.can_trade ? "yes" : "no"}`
          : account.message || "Binance is not connected",
      });
    } catch (error) {
      setNotice({ ok: false, text: getErrorMessage(error) });
    }
  };

  const selectAccount = async (accountId) => {
    if (!onSelectViewAccount || accountId === selectedViewAccountId) return;
    setSwitchingAccount(true);
    setNotice(null);
    setConfirmKill(false);
    setConfirmResume(false);
    setConfirmStart(false);
    setConfirmStop(false);
    try {
      await onSelectViewAccount(accountId);
      setNotice({ ok: true, text: `Now viewing ${accountId}` });
    } catch (error) {
      setNotice({ ok: false, text: `Account switch failed: ${getErrorMessage(error)}` });
    } finally {
      setSwitchingAccount(false);
    }
  };

  return (
    <header className="topbar" data-testid="app-header">
      <div className="topbar-context">
        <div className={`topbar-engine-dot ${running ? "running" : "idle"}`}><Activity size={14} /></div>
        <div>
          <p className="topbar-kicker">{status?.strategy || "Loading strategy"}</p>
          <div className="topbar-title-row">
            <h1>Futures command center</h1>
            <span className={`mode-badge mode-badge-${mode}`}><CircleDot size={9} /> {mode}</span>
            <span
              className={`account-runtime-badge ${runtimeTruth.executionTone}`}
              data-testid="selected-account-runtime"
              title={runtimeTruth.executionDetail}
            >
              {runtimeTruth.accountName} · {runtimeTruth.executionLabel}
            </span>
          </div>
        </div>
      </div>
      <div className="topbar-actions">
        {notice && (
          <span className={`topbar-notice ${notice.ok ? "notice-success" : "notice-danger"}`} data-testid="header-action-message" title={notice.text}>
            {notice.ok ? <Check size={13} /> : <AlertOctagon size={13} />} {notice.text}
            <button onClick={() => setNotice(null)} aria-label="Dismiss message"><X size={12} /></button>
          </span>
        )}
        <span className="last-updated" title={`Exchange UTC: ${updatedUtc}`}>
          <span className="status-dot online" /> Updated {updatedIst}
        </span>
        <span className={`connection-badge ${feedTone}`} data-testid="ws-status"><Wifi size={12} /> {feedState}</span>
        <button className="topbar-icon-button" onClick={refresh} title="Refresh dashboard" aria-label="Refresh dashboard"><RefreshCw size={15} /></button>
        <button data-testid="exchange-status-btn" onClick={verifyExchange} className="topbar-control topbar-control-blue"><ShieldCheck size={13} /> {isPaperAccount ? "Paper simulator" : `Binance ${accountType}`}</button>
        <button data-testid="open-backtest-btn" onClick={onBacktest} className="topbar-control topbar-control-violet"><FlaskConical size={13} /> Backtest</button>
        <div className="flex items-center gap-2 bg-zinc-900/90 border border-zinc-800 rounded-lg px-2.5 py-1 text-xs font-mono text-zinc-200" data-testid="account-selector-container">
          <span className="text-[10px] text-zinc-400 uppercase tracking-wider font-semibold">View Account:</span>
          <CircleDot size={13} className={selectedViewAccountId === "paper" ? "text-cyan-400" : "text-emerald-400"} />
          <select
            value={selectedViewAccountId}
            onChange={(e) => selectAccount(e.target.value)}
            title="View Account dashboard state"
            className="bg-transparent text-zinc-100 font-mono focus:outline-none cursor-pointer text-xs pr-1 font-medium"
            data-testid="account-selector-dropdown"
            disabled={switchingAccount}
          >
            <option value="paper" className="bg-zinc-900 text-zinc-100">📑 Paper Account (Simulation)</option>
            {!accounts.some((acc) => acc.id === selectedViewAccountId) && selectedViewAccountId !== "paper" && (
              <option value={selectedViewAccountId} className="bg-zinc-900 text-zinc-100">
                ⚡ {selectedViewAccountId} (Loading)
              </option>
            )}
            {accounts.filter((acc) => acc.id !== "paper").map((acc) => (
              <option key={acc.id} value={acc.id} className="bg-zinc-900 text-zinc-100">
                ⚡ {acc.name || acc.id} ({(acc.account_type || acc.mode || "account").toUpperCase()})
              </option>
            ))}
          </select>
          {switchingAccount && <RefreshCw size={12} className="animate-spin text-sky-400" aria-label="Switching account" />}
          {accountsError && <AlertOctagon size={12} className="text-amber-400" aria-label="Account registry unavailable" title={accountsError} />}
        </div>
        {running ? (
          confirmStop ? (
            <>
              <button data-testid="confirm-stop-btn" onClick={() => { setConfirmStop(false); act(`/accounts/${accountPathId}/stop`, "Stop"); }} className="topbar-control topbar-control-warning strong">Confirm stop</button>
              <button data-testid="cancel-stop-btn" onClick={() => setConfirmStop(false)} className="topbar-control">Cancel</button>
            </>
          ) : (
            <button data-testid="stop-bot-btn" onClick={() => setConfirmStop(true)} className="topbar-control topbar-control-warning">Stop</button>
          )
        ) : (
          confirmStart ? (
            <>
              <button data-testid="confirm-start-btn" onClick={() => { setConfirmStart(false); act(`/accounts/${accountPathId}/start`, "Start"); }} className="topbar-control topbar-control-success strong">Confirm start</button>
              <button data-testid="cancel-start-btn" onClick={() => setConfirmStart(false)} className="topbar-control">Cancel</button>
            </>
          ) : (
            <button
              data-testid="start-bot-btn"
              onClick={() => setConfirmStart(true)}
              className="topbar-control topbar-control-success"
              disabled={!runtimeTruth.loaded || runtimeTruth.riskLocked}
              title={runtimeTruth.riskLocked ? runtimeTruth.executionDetail : "Start selected account"}
            >
              Start
            </button>
          )
        )}
        {runtimeTruth.entryPaused && !kill && (
          <button
            data-testid="reset-loss-streak-header-btn"
            onClick={() => act(`/accounts/${accountPathId}/reset-loss-streak`, "Reset loss streak")}
            className="topbar-control topbar-control-warning strong"
            title={status?.risk?.entry_pause_reason || "Loss streak pause active. Click to reset streak and resume entries."}
          >
            <RotateCcw size={12} /> Reset streak
          </button>
        )}
        {needsReconciliation ? (
          <button data-testid="reconcile-btn" onClick={() => act(`/accounts/${accountPathId}/reconcile`, "Reconcile")} className="topbar-control topbar-control-warning">Reconcile</button>
        ) : kill ? (
          <>
            {confirmResume ? (
              <>
                <button data-testid="confirm-resume-btn" onClick={() => { setConfirmResume(false); act(`/accounts/${accountPathId}/resume`, "Resume"); }} className="topbar-control topbar-control-success strong">Confirm resume</button>
                <button data-testid="cancel-resume-btn" onClick={() => setConfirmResume(false)} className="topbar-control">Cancel</button>
              </>
            ) : (
              <button data-testid="resume-btn" onClick={() => setConfirmResume(true)} className="topbar-control topbar-control-success strong">Resume</button>
            )}
          </>
        ) : confirmKill ? (
          <>
            <button data-testid="confirm-kill-btn" onClick={() => { setConfirmKill(false); act(`/accounts/${accountPathId}/kill`, "Kill"); }} className="topbar-control topbar-control-danger strong">Confirm kill</button>
            <button data-testid="cancel-kill-btn" onClick={() => setConfirmKill(false)} className="topbar-control">Cancel</button>
          </>
        ) : (
          <button data-testid="kill-switch-btn" onClick={() => setConfirmKill(true)} className="topbar-control topbar-control-danger strong"><AlertOctagon size={13} /> Kill switch</button>
        )}
        <button className="topbar-settings-button" onClick={onSettings} data-testid="open-settings-btn"><Settings2 size={16} /></button>
      </div>
    </header>
  );
}
